import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Info, Layers, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const CATEGORY_GROUPS = {
  "Model Aeroplanes": [
    "Model Kits",
    "RC ARF Models",
    "Control Line Kits",
    "Display Model Kits",
    "3D Aerobatic Models",
    "Seagull ARF Models",
    "FMS PNP Models",
    "Chuck & Catapult Gliders",
  ],
  Engines: ["Diesel Engines", "Glow Plug Engines", "Petrol/Gas Engines"],
};

const GROUPED_SET = new Set(Object.values(CATEGORY_GROUPS).flat());

export const ProductCatalog = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [activeSub, setActiveSub] = useState("All");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/catalog")
      .then((r) => {
        if (r.data.items?.length) {
          setItems(r.data.items);
          setCategories(r.data.categories);
        }
      })
      .catch(() => {});
  }, []);

  const FILTERS = useMemo(() => {
    const top = ["All"];
    const groupPlaced = new Set();
    categories.forEach((cat) => {
      const group = Object.keys(CATEGORY_GROUPS).find((g) => CATEGORY_GROUPS[g].includes(cat));
      if (group) {
        if (!groupPlaced.has(group)) {
          groupPlaced.add(group);
          top.push(group);
        }
      } else {
        top.push(cat);
      }
    });
    return top;
  }, [categories]);

  const subFilters = useMemo(() => {
    if (!CATEGORY_GROUPS[active]) return null;
    return CATEGORY_GROUPS[active].filter((c) => categories.includes(c));
  }, [active, categories]);

  const selectTop = (cat) => {
    setActive(cat);
    setActiveSub("All");
  };

  const filtered = useMemo(() => {
    let list = items;
    if (active !== "All") {
      if (CATEGORY_GROUPS[active]) {
        if (activeSub !== "All") list = list.filter((p) => p.category === activeSub);
        else {
          const set = new Set(CATEGORY_GROUPS[active]);
          list = list.filter((p) => set.has(p.category));
        }
      } else {
        list = list.filter((p) => p.category === active);
      }
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.tagline || "").toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [active, activeSub, query, items]);

  const showingLabel = query.trim()
    ? ` matching “${query.trim()}”`
    : active === "All"
    ? " across all categories"
    : ` in ${activeSub !== "All" ? activeSub : active}`;

  return (
    <section data-testid="product-catalog" className="bg-aero-deep">
      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-24">
        {/* Filter bar */}
        <div className="flex flex-col gap-6 border-b border-steel/20 pb-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="flex items-center gap-2 font-heading text-xs uppercase tracking-[0.28em] text-saffron">
              <Layers className="h-4 w-4" /> Full Catalogue
            </span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Browse the complete range
            </h2>
            <p className="mt-2 font-body text-sm text-steel">
              Showing{" "}
              <span className="text-ink" data-testid="catalog-count">
                {filtered.length}
              </span>{" "}
              {filtered.length === 1 ? "product" : "products"}
              {showingLabel}.
            </p>
          </div>

          <div className="flex flex-col items-start gap-3 lg:items-end">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-steel/60" />
              <input
                data-testid="catalog-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products…"
                className="w-full rounded-full border border-steel/30 bg-aero-surface py-2.5 pl-11 pr-10 font-body text-sm text-ink outline-none transition-colors placeholder:text-steel/60 focus:border-saffron"
              />
              {query && (
                <button
                  data-testid="catalog-search-clear"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-steel transition-colors hover:text-saffron"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end" data-testid="catalog-filters">
            {FILTERS.map((cat) => {
              const isActive = active === cat;
              return (
                <button
                  key={cat}
                  onClick={() => selectTop(cat)}
                  data-testid={`catalog-filter-${slug(cat)}`}
                  className={`relative rounded-full border px-4 py-2 font-heading text-xs font-semibold uppercase tracking-wide transition-all duration-300 ${
                    isActive
                      ? "border-saffron bg-saffron text-white"
                      : "border-steel/30 text-steel hover:border-saffron/60 hover:text-ink"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
            </div>
          </div>
        </div>

        {/* Subcategory bar */}
        {subFilters && (
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="catalog-subfilters">
            <span className="mr-1 font-heading text-[11px] font-semibold uppercase tracking-[0.2em] text-steel/70">
              {active}:
            </span>
            {["All", ...subFilters].map((sub) => {
              const isActive = activeSub === sub;
              return (
                <button
                  key={sub}
                  onClick={() => setActiveSub(sub)}
                  data-testid={`catalog-subfilter-${slug(sub)}`}
                  className={`rounded-full border px-3.5 py-1.5 font-heading text-[11px] font-semibold uppercase tracking-wide transition-all duration-300 ${
                    isActive
                      ? "border-aero-navy bg-aero-navy text-white"
                      : "border-steel/25 bg-aero-surface text-steel hover:border-saffron/60 hover:text-ink"
                  }`}
                >
                  {sub}
                </button>
              );
            })}
          </div>
        )}

        {/* Grid */}
        {filtered.length === 0 && items.length > 0 && (
          <p data-testid="catalog-no-results" className="mt-12 rounded-xl border border-steel/20 bg-aero-surface p-10 text-center font-body text-sm text-steel">
            No products match your search. Try a different keyword or clear the search.
          </p>
        )}
        <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.button
                type="button"
                key={p.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelected(p)}
                data-testid={`catalog-card-${p.id}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-steel/20 bg-aero-surface text-left transition-all duration-500 hover:-translate-y-1 hover:border-saffron/60"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-white">
                  {p.image && (
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  <span className="absolute left-4 top-4 rounded-full border border-saffron/40 bg-white/90 px-3 py-1 font-heading text-[10px] uppercase tracking-wide text-saffron backdrop-blur">
                    {p.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-heading text-lg font-semibold text-ink">{p.name}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-steel">
                    {p.tagline}
                  </p>
                  <dl className="mt-4 space-y-1.5 border-t border-steel/15 pt-4">
                    {p.specs.slice(0, 3).map((s) => (
                      <div key={s.label} className="flex items-center justify-between gap-3">
                        <dt className="font-body text-xs text-steel">{s.label}</dt>
                        <dd className="text-right font-body text-xs font-medium text-ink">
                          {s.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-heading text-sm font-semibold text-saffron">
                    View Details
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          data-testid="catalog-detail-dialog"
          className="max-h-[90vh] max-w-3xl overflow-y-auto border-steel/25 bg-aero-surface p-0 text-ink"
        >
          {selected && (
            <div className="grid md:grid-cols-2">
              <div className="relative h-56 w-full overflow-hidden bg-white md:h-full">
                {selected.image && (
                  <img
                    src={selected.image}
                    alt={selected.name}
                    className="h-full w-full object-contain"
                  />
                )}
                <span className="absolute left-4 top-4 rounded-full border border-saffron/40 bg-white/90 px-3 py-1 font-heading text-[10px] uppercase tracking-wide text-saffron backdrop-blur">
                  {selected.category}
                </span>
              </div>

              <div className="p-6 sm:p-8">
                <DialogHeader className="text-left">
                  <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-ink">
                    {selected.name}
                  </DialogTitle>
                </DialogHeader>
                <p className="mt-3 font-body text-sm leading-relaxed text-steel">
                  {selected.description}
                </p>

                <div className="mt-6">
                  <h4 className="font-heading text-xs uppercase tracking-[0.2em] text-saffron">
                    Specifications
                  </h4>
                  <dl className="mt-3 divide-y divide-steel/15 rounded-lg border border-steel/20">
                    {selected.specs.map((s) => (
                      <div key={s.label} className="flex items-center justify-between gap-4 px-4 py-2.5">
                        <dt className="font-body text-xs text-steel">{s.label}</dt>
                        <dd className="text-right font-body text-sm font-medium text-ink">
                          {s.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="mt-5 flex items-start gap-2 rounded-lg border border-saffron/25 bg-aero-navy/60 p-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
                  <p className="font-body text-xs text-steel">
                    Pricing &amp; availability shared on request. Get in touch for a quote
                    or bulk / NCC orders.
                  </p>
                </div>

                <Link
                  to="/contact"
                  data-testid="catalog-enquire-btn"
                  className="group mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-saffron px-6 py-3.5 font-heading font-semibold text-white transition-all duration-300 hover:bg-saffron-soft"
                >
                  Enquire About This Product
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
